import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Plus, Users, TrendingUp, Gem, Heart, Tv, RefreshCw, MapPin, Check, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country options for streaming availability
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

const FreshPastures = ({ user, setUser }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [countryCode, setCountryCode] = useState('US');
  const [basedOn, setBasedOn] = useState(null);
  const [addingShow, setAddingShow] = useState(null);
  const [addedShows, setAddedShows] = useState(new Set());

  useEffect(() => {
    // Try to detect user's country from browser
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      const langCountry = browserLang.split('-')[1];
      if (langCountry && COUNTRIES.find(c => c.code === langCountry)) {
        setCountryCode(langCountry);
      }
    }
    loadRecommendations();
  }, []);

  const loadRecommendations = async (refresh = false) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await axios.get(`${API}/recommendations/fresh-pastures`, {
        params: { country_code: countryCode },
        withCredentials: true
      });
      setRecommendations(response.data.recommendations || []);
      setBasedOn(response.data.based_on);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCountryChange = (newCountry) => {
    setCountryCode(newCountry);
    // Reload recommendations with new country
    setTimeout(() => loadRecommendations(true), 100);
  };

  const handleAddShow = async (rec) => {
    setAddingShow(rec.tmdb_id);
    try {
      // Build show data matching the main shows endpoint format
      const showData = {
        title: rec.title,
        image_url: rec.poster_path ? `https://image.tmdb.org/t/p/w500${rec.poster_path}` : null,
        backdrop_url: rec.backdrop_path ? `https://image.tmdb.org/t/p/w780${rec.backdrop_path}` : null,
        release_date: rec.release_date || null,
        genres: null,
        overview: rec.overview || null,
        status: 'Want to Watch',
        streaming_service: null,
        custom_service: null,
        personal_rating: null,
        personal_review: null,
        tmdb_id: rec.tmdb_id,
        media_type: rec.media_type || 'tv'
      };

      await axios.post(`${API}/shows`, showData, {
        withCredentials: true
      });
      toast.success(`"${rec.title}" added to your watchlist!`);
      setAddedShows(prev => new Set([...prev, rec.tmdb_id]));
    } catch (error) {
      if (error.response?.status === 409 || error.response?.status === 400) {
        toast.error('You already have this show');
        setAddedShows(prev => new Set([...prev, rec.tmdb_id]));
      } else {
        toast.error('Failed to add show');
      }
    } finally {
      setAddingShow(null);
    }
  };

  const getMatchIcon = (matchType) => {
    switch (matchType) {
      case 'friend_recommended':
        return <Users size={16} className="match-icon" />;
      case 'similar_to_favorite':
        return <Heart size={16} className="match-icon" />;
      case 'hidden_gem':
        return <Gem size={16} className="match-icon" />;
      case 'genre_match':
        return <TrendingUp size={16} className="match-icon" />;
      default:
        return <Sparkles size={16} className="match-icon" />;
    }
  };

  const getMatchLabel = (matchType) => {
    switch (matchType) {
      case 'friend_recommended':
        return 'Flock Pick';
      case 'similar_to_favorite':
        return 'Similar to Your Favorites';
      case 'hidden_gem':
        return 'Hidden Gem';
      case 'genre_match':
        return 'Genre Match';
      default:
        return 'Recommended';
    }
  };

  const selectedCountry = COUNTRIES.find(c => c.code === countryCode);

  return (
    <>
      <SEO 
        title="Graze - Discover New Shows | baad.tv"
        description="Get AI-powered TV show recommendations based on your viewing history, ratings, and what your flock is watching."
        url="/fresh-pastures"
      />
      <div className="fresh-pastures-container">
        <Navigation user={user} setUser={setUser} currentPage="fresh-pastures" />
        
        <div className="fresh-pastures-content">
          <div className="page-header">
            <div className="header-text">
              <h1 data-testid="fresh-pastures-title">
                <Sparkles className="header-icon" />
                Graze
              </h1>
              <p>Discover your next binge-worthy show</p>
            </div>
            
            <div className="header-actions">
              <div className="country-selector">
                <MapPin size={16} />
                <Select value={countryCode} onValueChange={handleCountryChange}>
                  <SelectTrigger className="country-trigger" data-testid="country-select">
                    <SelectValue>
                      {selectedCountry?.flag} {selectedCountry?.code}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={() => loadRecommendations(true)} 
                disabled={refreshing}
                className="refresh-btn"
                data-testid="refresh-recommendations"
              >
                <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
                {refreshing ? 'Finding shows...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {basedOn && !loading && (
            <div className="based-on-info">
              <span>Recommendations based on</span>
              <span className="stat">{basedOn.your_shows} shows</span>
              <span className="stat">{basedOn.your_reviews} reviews</span>
              {basedOn.friends_input > 0 && (
                <span className="stat flock">{basedOn.friends_input} flock picks</span>
              )}
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Finding fresh shows for you...</p>
              <p className="loading-subtext">Analyzing your taste and flock recommendations</p>
            </div>
          ) : recommendations.length === 0 ? (
            <Card className="empty-state-card">
              <CardContent className="empty-state">
                <Tv size={48} />
                <h3>No recommendations yet</h3>
                <p>Add some shows to your collection and rate them to get personalized recommendations!</p>
                <Button onClick={() => window.location.href = '/my-shows'}>
                  Add Shows
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="recommendations-grid">
              {recommendations.map((rec) => (
                <Card key={rec.id} className="recommendation-card" data-testid={`rec-${rec.tmdb_id}`}>
                  <div className="card-image-container">
                    {rec.poster_path ? (
                      <img 
                        src={`https://image.tmdb.org/t/p/w500${rec.poster_path}`}
                        alt={rec.title}
                        className="card-poster"
                      />
                    ) : (
                      <div className="no-poster">
                        <Tv size={40} />
                      </div>
                    )}
                    <div className="match-badge">
                      {getMatchIcon(rec.match_type)}
                      <span>{getMatchLabel(rec.match_type)}</span>
                    </div>
                    {rec.vote_average && (
                      <div className="tmdb-rating">
                        ⭐ {rec.vote_average.toFixed(1)}
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="card-content">
                    <h3 className="show-title">{rec.title}</h3>
                    
                    {rec.overview && (
                      <p className="show-overview">{rec.overview}</p>
                    )}
                    
                    <p className="reason">
                      "{rec.reason}"
                    </p>
                    
                    {rec.friend_rating && (
                      <div className="friend-rating">
                        <Users size={14} />
                        <span>
                          Your flock rated it <strong>{rec.friend_rating.avg_rating}</strong>
                          {rec.friend_rating.num_friends > 1 && ` (${rec.friend_rating.num_friends} friends)`}
                        </span>
                      </div>
                    )}
                    
                    <div className="streaming-info">
                      {rec.streaming.available_in_country ? (
                        <div className="available">
                          <Check size={14} />
                          <span>
                            Available on {rec.streaming.providers.slice(0, 2).map(p => p.name).join(', ')}
                            {rec.streaming.providers.length > 2 && ` +${rec.streaming.providers.length - 2}`}
                          </span>
                        </div>
                      ) : (
                        <div className="not-available">
                          <MapPin size={14} />
                          <span>May not be available in {selectedCountry?.name}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="card-actions">
                      {addedShows.has(rec.tmdb_id) ? (
                        <Button disabled className="added-btn">
                          <Check size={16} />
                          Added
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleAddShow(rec)}
                          disabled={addingShow === rec.tmdb_id}
                          className="add-btn"
                          data-testid={`add-${rec.tmdb_id}`}
                        >
                          {addingShow === rec.tmdb_id ? (
                            <>Adding...</>
                          ) : (
                            <>
                              <Plus size={16} />
                              Add to Watchlist
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <style jsx>{`
          .fresh-pastures-container {
            min-height: 100vh;
            background: #121212;
          }

          .fresh-pastures-content {
            max-width: 1400px;
            margin: 0 auto;
            padding: 2rem;
          }

          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            gap: 1rem;
          }

          .header-text h1 {
            font-size: 2.5rem;
            font-weight: 700;
            color: #ffffff;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 0.5rem;
          }

          .header-text h1 :global(.header-icon) {
            color: #E62F88;
          }

          .header-text p {
            color: #b0b0b0;
            font-size: 1.1rem;
          }

          .header-actions {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .country-selector {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #b0b0b0;
          }

          .country-selector :global(.country-trigger) {
            background: #2a2a2a;
            border: 1px solid #333;
            color: #ffffff;
            min-width: 100px;
          }

          .refresh-btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background: #E62F88 !important;
            color: #000 !important;
          }

          .refresh-btn :global(.spinning) {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .based-on-info {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
            background: #1a1a1a;
            border-radius: 8px;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            font-size: 0.9rem;
            color: #b0b0b0;
          }

          .based-on-info .stat {
            background: #2a2a2a;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            color: #ffffff;
          }

          .based-on-info .stat.flock {
            background: rgba(230, 47, 136, 0.2);
            color: #E62F88;
          }

          .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 4rem 2rem;
            color: #ffffff;
          }

          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(230, 47, 136, 0.3);
            border-top-color: #E62F88;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 1.5rem;
          }

          .loading-state p {
            font-size: 1.2rem;
            margin-bottom: 0.5rem;
          }

          .loading-subtext {
            color: #b0b0b0;
            font-size: 0.9rem !important;
          }

          .empty-state-card {
            max-width: 500px;
            margin: 4rem auto;
            background: #1a1a1a;
            border: 1px solid #333;
          }

          .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 3rem !important;
            color: #b0b0b0;
          }

          .empty-state h3 {
            color: #ffffff;
            margin: 1rem 0 0.5rem;
          }

          .empty-state p {
            margin-bottom: 1.5rem;
          }

          .recommendations-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1.5rem;
          }

          .recommendation-card {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 12px;
            overflow: hidden;
            transition: transform 0.2s, border-color 0.2s;
          }

          .recommendation-card:hover {
            transform: translateY(-4px);
            border-color: #E62F88;
          }

          .card-image-container {
            position: relative;
            aspect-ratio: 2/3;
            background: #2a2a2a;
          }

          .card-poster {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .no-poster {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #666;
          }

          .match-badge {
            position: absolute;
            top: 12px;
            left: 12px;
            display: flex;
            align-items: center;
            gap: 0.4rem;
            background: rgba(0, 0, 0, 0.85);
            padding: 0.4rem 0.75rem;
            border-radius: 20px;
            font-size: 0.75rem;
            color: #E62F88;
            font-weight: 600;
          }

          .tmdb-rating {
            position: absolute;
            top: 12px;
            right: 12px;
            background: rgba(0, 0, 0, 0.85);
            padding: 0.4rem 0.6rem;
            border-radius: 6px;
            font-size: 0.85rem;
            color: #ffffff;
            font-weight: 600;
          }

          .card-content {
            padding: 1.25rem !important;
          }

          .show-title {
            font-size: 1.15rem;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 0.75rem;
            line-height: 1.3;
          }

          .show-overview {
            font-size: 0.85rem;
            color: #999;
            margin-bottom: 0.75rem;
            line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .reason {
            font-size: 0.9rem;
            color: #b0b0b0;
            font-style: italic;
            margin-bottom: 1rem;
            line-height: 1.5;
          }

          .friend-rating {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.85rem;
            color: #E62F88;
            margin-bottom: 0.75rem;
            padding: 0.5rem 0.75rem;
            background: rgba(230, 47, 136, 0.1);
            border-radius: 6px;
          }

          .friend-rating strong {
            color: #ffffff;
          }

          .streaming-info {
            font-size: 0.8rem;
            margin-bottom: 1rem;
          }

          .streaming-info .available {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            color: #4ade80;
          }

          .streaming-info .not-available {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            color: #f59e0b;
          }

          .card-actions {
            display: flex;
            gap: 0.5rem;
          }

          .add-btn {
            flex: 1;
            background: #E62F88 !important;
            color: #000 !important;
            font-weight: 600;
          }

          .added-btn {
            flex: 1;
            background: #2a2a2a !important;
            color: #4ade80 !important;
            border: 1px solid #4ade80 !important;
          }

          @media (max-width: 768px) {
            .fresh-pastures-content {
              padding: 1rem;
              padding-bottom: 100px;
            }

            .page-header {
              flex-direction: column;
              align-items: stretch;
            }

            .header-text h1 {
              font-size: 1.75rem;
            }

            .header-actions {
              justify-content: space-between;
            }

            .recommendations-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 1rem;
            }

            .card-image-container {
              aspect-ratio: 2/3;
            }

            .match-badge {
              font-size: 0.65rem;
              padding: 0.3rem 0.5rem;
            }

            .show-title {
              font-size: 0.95rem;
            }

            .reason {
              font-size: 0.8rem;
            }

            .friend-rating {
              font-size: 0.75rem;
            }

            .streaming-info {
              font-size: 0.7rem;
            }

            .card-content {
              padding: 0.875rem !important;
            }
          }

          @media (max-width: 480px) {
            .recommendations-grid {
              grid-template-columns: 1fr;
            }

            .header-actions {
              flex-direction: column;
              gap: 0.75rem;
            }

            .country-selector {
              width: 100%;
            }

            .country-selector :global(.country-trigger) {
              flex: 1;
            }

            .refresh-btn {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default FreshPastures;
